import { useContext, useEffect, useState } from 'react'
import { AxiosResponse } from 'axios'
import { CANCEL, DURATION, APPLY, UPDATE, FROM, TO, REASON, HOURS, DAYS, CANCEL_TIME_OFF_REQUEST, NOTE, CANCEL_TIME_OFF, REJECTING_TIME_OFF_REQUEST, REJECT_TIME_OFF } from '../../constants/strings'
import { checkPermission, calculateDuration, getStaffId, toPascalCase, getCurrentDateByTimeZone, convertToLocalByOffset, getAllDays } from '../../helpers/utils'
import { CommonCtx } from '../../context/CommonCtxProvider'
import { ErrorMessage, Field, Form, Formik, FieldProps } from 'formik'
import { getUsersList } from '../../helpers/manageUsersHelper'
import { PageMode, PermissionKeys, AlertVariant, SubPermissionKeys } from '../../constants/constants'
import { parseTimeForTimePicker } from '../../helpers/newsAndEventsHelper'
import { routes } from '../../constants/routes'
import { ScheduleManagementCtx } from '../../context/ScheduleManagementCtxProvider'
import { useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { AVAILABILITY_LIST, AVAILABILITY_REQUESTS_LIST, USERS_LIST, USERS_TIME_OFF_LIST } from '../../constants/queryKeys'
import DatePickerComponent from '../date_picker/DatePickerComponent'
import dayjs from 'dayjs'
import useMutationHook from '../../hooks/useMutationHook'
import useQueryHook from '../../hooks/useQueryHook'
import {
    addTimeOffRequest,
    fullDayValidationSchema,
    initialValuesForTimeOff,
    timeOffChangeRequest,
    timeOffValidationSchema,
    updateTimeOffRequest,
} from '../../helpers/timeOffHelper'

import {
    AddTimeOff,
    AddTimeOffResponse,
    OffDays,
    TimeOffRequestFormType,
    TimeOffRequestStatus,
    ToastMessageProps,
    UpdateTimeOff,
    Users,
} from '../../constants/staticTypes'
import { JTranslation, TranslationContext, jTranslationText } from '../../helpers/jTranslate'
import CommonModal from '../common_modal/CommonModal'
import MlxTimePicker from '../mlx_time_picker/MlxTimePicker'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import { Select } from 'antd'
import { getOffDays } from '../../helpers/availabilityHelper'
import { Message } from 'primereact/message'

type Props = {
    onClose: Function
    pageMode: PageMode.ADD | PageMode.EDIT
    setToastMessage: React.Dispatch<React.SetStateAction<ToastMessageProps>>
    parent?: string
    onTimeOffSuccess?: (message?: string, variant?: string) => void
}

const TimeOffRequestForm = ({ onClose, pageMode, setToastMessage, parent, onTimeOffSuccess }: Props) => {
    const queryClient = useQueryClient()
    const timeOffMutation = useMutationHook(queryClient, true)
    const submitButtonName = pageMode === PageMode.EDIT ? UPDATE : APPLY
    const location = useLocation()
    const currentPath = location.pathname
    // CONTEXT VARIABLE
    const { setShowSideBar } = useContext(CommonCtx)
    const { timeOffData, setTimeOffData } = useContext(ScheduleManagementCtx);
    const translationContext = useContext(TranslationContext)
    const { targetLanguage } = translationContext
    // STATE VARIABLES
    const [usersList, setUsersList] = useState<Users[]>([])
    const [userDropDownOptions, setDropDownOptions] = useState<{ label: string, value: string }[]>([])
    const [translationText, setTranslatedText] = useState<{
        startTime: string,
        endTime: string,
        loading: string,
        select: string,
        self: string,
    }>({
        startTime: 'Start Time',
        endTime: 'End Time',
        loading: 'Loading...',
        select: 'Select',
        self: 'Self',
    });
    const [rejectReason, setRejectReason] = useState('')
    const [showWarning, setShowWarning] = useState(false)
    const [warningType, setWarningType] = useState<'cancel' | 'reject' | null>(null)
    const [mandatoryDays, setMandatoryDays] = useState<dayjs.Dayjs[]>([])
    const [mandatoryError, setMandatoryError] = useState(false)

    // Get the current date
    const currentDate = getCurrentDateByTimeZone()
    // Calculate the maximum date, 5 years from now
    const maxDate = getCurrentDateByTimeZone()
    maxDate.setFullYear(currentDate.getFullYear() + 1)
    const staffId = getStaffId()

    const canManageTimeOff = (
        checkPermission(queryClient, PermissionKeys.MANAGE_TIME_OFF, SubPermissionKeys.ADD) ||
        checkPermission(queryClient, PermissionKeys.MANAGE_TIME_OFF, SubPermissionKeys.EDIT)
    );

    // Translate on load and language switch
    useEffect(() => {
        const fetchTranslation = async () => {
            try {
                const translations = await Promise.all([
                    jTranslationText({ text: 'Start Time', typeCase: 'pascal', translationContext }),
                    jTranslationText({ text: 'End Time', typeCase: 'pascal', translationContext }),
                    jTranslationText({ text: 'Loading...', typeCase: 'pascal', translationContext }),
                    jTranslationText({ text: 'Select', typeCase: 'pascal', translationContext }),
                    jTranslationText({ text: 'Self', typeCase: 'pascal', translationContext }),
                ])

                setTranslatedText({
                    startTime: translations[0] ?? 'Start Time',
                    endTime: translations[1] ?? 'End Time',
                    loading: translations[2] ?? 'Loading...',
                    select: translations[3] ?? 'Select',
                    self: translations[4] ?? 'Self',
                })
            } catch {
                setTranslatedText({
                    startTime: 'Start Time',
                    endTime: 'End Time',
                    loading: 'Loading...',
                    select: 'Select',
                    self: 'Self',
                })
            }
        }
        fetchTranslation()
    }, [targetLanguage])

    useEffect(() => {
        if (currentPath === routes.time_off && pageMode === PageMode.ADD) {
            setTimeOffData({ ...initialValuesForTimeOff, staffId })
        }

        return () => {
            setTimeOffData(initialValuesForTimeOff)
            setWarningType(null)
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        const userDropDownOptions: { label: string, value: string }[] = usersList?.map((item) => {
            return {
                label: toPascalCase(item?.preferredName),
                value: item.id
            }
        });
        setDropDownOptions(userDropDownOptions)
    }, [usersList])

    useEffect(() => {
        calculateDuration({
            type: timeOffData.type,
            endTime: timeOffData.endTime,
            startTime: timeOffData.startTime,
            fromDate: timeOffData.fromDate,
            toDate: timeOffData.toDate,
        })
    }, [timeOffData])

    const getValidationSchema = () => {
        if (timeOffData.type === 'full-day') {
            return fullDayValidationSchema
        } else {
            return timeOffValidationSchema
        }
    }

    // users list fetch success
    const onUserListFetchSuccess = (res: AxiosResponse<any, any>) => {
        const responseData = res?.data?.data.lists as Users[]
        setUsersList(responseData)
    }

    // fetch users list
    const userData = useQueryHook(
        USERS_LIST,
        () => getUsersList(`?page=1&limit=500&sort[user.firstName]=ASC`),
        onUserListFetchSuccess
    )

    // update mandatory days
    const updateOffDays = (offDays: OffDays) => {
        // get all mandatory days
        let currentAllMandatoryDays: dayjs.Dayjs[] = []
        for (const day of offDays.mandatoryDays) {
            const allMandatoryDays = dayjs(day.startDate).isSame(dayjs(day.endDate))
                ? [dayjs(day.startDate)]
                : getAllDays(dayjs(day.startDate), dayjs(day.endDate))

            for (const date of allMandatoryDays) {
                // Check if the date is not already in the array
                if (!currentAllMandatoryDays.some((existingDate) => existingDate.isSame(date))) {
                    currentAllMandatoryDays.push(date)
                }
            }
        }
        setMandatoryDays(currentAllMandatoryDays)
        // console.log(currentAllMandatoryDays)
    }

    const validate = (values: TimeOffRequestFormType) => {
        // check if mandatory days are available
        const fromDate = convertToLocalByOffset(values.fromDate, { type: 'string', format: 'YYYY-MM-DD' })
        const toDate = convertToLocalByOffset(values.type === 'full-day' ? values.toDate : values.fromDate, { type: 'string', format: 'YYYY-MM-DD' })
        const timeOffDays = getAllDays(dayjs(fromDate), dayjs(toDate));
        const isMandatoryDayAvailable = mandatoryDays.some((mandatoryDay) => timeOffDays.some((timeOffDay) => timeOffDay.isSame(mandatoryDay)))
        if(isMandatoryDayAvailable) {
            setMandatoryError(true)
        } else {
            setMandatoryError(false)
        }

        setTimeOffData((prevValues) => {
            return {
                ...prevValues,
                type: values.type,
                fromDate: convertToLocalByOffset(values.fromDate, { type: 'date' }),
                toDate: convertToLocalByOffset(values.toDate, { type: 'date' }),
                startTime: values.startTime,
                endTime: values.endTime,
                duration: calculateDuration({
                    type: values.type,
                    endTime: values.endTime,
                    startTime: values.startTime,
                    fromDate: values.fromDate,
                    toDate: values.toDate,
                }),
                reason: values.reason,
                note: values.note,
                staffId: values.staffId,
            }
        })
    }

    const handleSubmit = (values: TimeOffRequestFormType) => {
        const { endTime, fromDate, note, reason, startTime, toDate, type, staffId, action } = values

        const commonParams = {
            startDate: dayjs(fromDate).format('YYYY-MM-DD'),
            endDate: type !== 'full-day' ? dayjs(fromDate).format('YYYY-MM-DD') : dayjs(toDate).format('YYYY-MM-DD'),
            startTime: type !== 'full-day' && dayjs(startTime).isValid() ? dayjs(startTime).format('HH:mm') : null,
            endTime: type !== 'full-day' && dayjs(endTime).isValid() ? dayjs(endTime).format('HH:mm') : null,
            note,
            reason,
            staffId,
            type,
        }

        if (action === 'approve') {
            // update time off request
            if (parent === 'scheduler' && pageMode === PageMode.EDIT) {
                const updateParams = {
                    ...commonParams,
                    id: timeOffData.id!,
                }
                updateTimeOffRequest(timeOffMutation, updateParams as UpdateTimeOff, () => { }, onError)
            }

            const params: TimeOffRequestStatus = { id: timeOffData.id!, newStatus: 'APPROVED', note: rejectReason }
            // approve api call
            timeOffChangeRequest(timeOffMutation, params, onSuccess, onError)
            return
        }

        if (action === 'cancel' || action === 'reject') {
            setShowWarning(true)
            return
        }

        if (pageMode === PageMode.ADD) {
            const addParams = {
                ...commonParams,
                isManaged: parent === 'scheduler'
            }
            addTimeOffRequest(timeOffMutation, addParams as AddTimeOff, onSuccess, onError)
        } else {
            const updateParams = {
                ...commonParams,
                id: timeOffData.id!,
            }
            updateTimeOffRequest(timeOffMutation, updateParams as UpdateTimeOff, onSuccess, onError)
        }
    }

    const onSuccess = (res: AddTimeOffResponse) => {
        if (parent === 'scheduler') {
            setToastMessage({ message: res.message, show: true, variant: AlertVariant.SUCCESS })
            queryClient.refetchQueries([AVAILABILITY_LIST])
            queryClient.refetchQueries([AVAILABILITY_REQUESTS_LIST])
            setShowWarning(false)
            setRejectReason('')
            if (onTimeOffSuccess)
                onTimeOffSuccess()
            onClose()
            return
        }
        queryClient.fetchQuery(USERS_TIME_OFF_LIST)
        setShowSideBar(false)
        setToastMessage({ message: res.message, show: true, variant: AlertVariant.SUCCESS })
    }

    const onError = (message: string) => {
        setToastMessage({ message: message, show: true, variant: AlertVariant.ERROR })
    }

    const rejectRequestModalContent = (
        <>
            <div className=''><JTranslation typeCase="capitalize" text={warningType === 'cancel' ? CANCEL_TIME_OFF_REQUEST : REJECTING_TIME_OFF_REQUEST} /></div>
            <p className="mt-3 mb-0">
                <JTranslation typeCase="pascal" text={NOTE} />
                <span className="mandatory ">*</span>
            </p>
            <textarea
                name="reject-reason"
                className="form-control reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
            />
        </>
    )

    const closeRejectionModal = () => {
        setShowWarning(false)
        setRejectReason('')
    }

    const cancelConfirmed = () => {
        const params: TimeOffRequestStatus = { id: timeOffData.id!, newStatus: warningType === 'cancel' ? 'CANCELLED' : 'REJECTED', note: rejectReason }
        // approve api call
        timeOffChangeRequest(timeOffMutation, params, onSuccess, onError)
    }

    const mandatoryDaysQuery = useQuery({
        queryKey: [
            'mandatory-days-for-time-off', 
            convertToLocalByOffset(currentDate, { type: 'string', format: 'YYYY-MM-DD' }), 
            convertToLocalByOffset(maxDate, { type: 'string', format: 'YYYY-MM-DD' })
        ],
        queryFn: () => {
            const startDate = convertToLocalByOffset(currentDate, { type: 'string', format: 'YYYY-MM-DD' }) as string
            const endDate = convertToLocalByOffset(maxDate, { type: 'string', format: 'YYYY-MM-DD' }) as string
            return getOffDays({ startDate, endDate, staffId })
        },
        onSuccess: (res: AxiosResponse<any, any>) => {
            const offDays = res.data.data as OffDays
            updateOffDays(offDays)
        },
        onError: (error) => {
            console.error('Failed to fetch off days:', error)
        },
        enabled: !!currentDate && !!maxDate,
        refetchOnWindowFocus: false,
    })

    return (
        <>
            <ConfirmDialog tagKey='time-off-confirm-request' />
            <CommonModal
                show={showWarning}
                modalContent={rejectRequestModalContent}
                title={warningType === 'cancel' ? CANCEL_TIME_OFF : REJECT_TIME_OFF}
                modalSize="md"
                disableProceed={!rejectReason}
                onHide={closeRejectionModal}
                callback={cancelConfirmed}
                hideCloseButton
            />

            <Formik
                enableReinitialize={true}
                initialValues={{ ...timeOffData, action: 'submit' }}
                onSubmit={handleSubmit}
                validate={validate}
                validateOnChange={true}
                validationSchema={getValidationSchema}
            >
                {(
                    { errors, touched, setFieldValue, handleSubmit, isSubmitting } // nosonar
                ) => (
                    <Form>
                        <div className="mb-3">
                            <label className="form-label d-flex">
                                <JTranslation typeCase="pascal" text={DURATION} />
                                <span className="mandatory ">*</span>
                            </label>
                            <Field
                                style={errors.type && touched.type ? { border: '1px solid red' } : undefined}
                                name="type"
                                as="select"
                                className="form-control"
                                type="text"
                                value={timeOffData.type}
                                autoComplete="off"
                                maxLength="250"
                            >
                                {/* Dropdown options */}
                                <option key="full-day" value="full-day">
                                    Full Day
                                </option>
                                <option key="time-off" value="time-off">
                                    Hours
                                </option>
                            </Field>

                            <ErrorMessage className="formik-error" name="type" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                        </div>
                        {
                            canManageTimeOff && (
                                <div className="mb-3">
                                    <label className="form-label d-flex">
                                        <JTranslation typeCase="pascal" text={"User"} />
                                        <span className="mandatory ">*</span>
                                    </label>
                                    <Select
                                        key={`time-off-user-dropdown-${timeOffData.id}`}
                                        showSearch
                                        value={(userData.isLoading || userData.isFetching) ? null : timeOffData.staffId}
                                        onChange={(value) => setFieldValue('staffId', value)}
                                        options={(currentPath === routes.time_off_requests || parent === 'scheduler') ? userDropDownOptions : [{label: translationText.self, value: staffId}] }
                                        placeholder={(userData.isLoading || userData.isFetching) ? translationText.loading : translationText.select}
                                        disabled={userData.isFetching || parent === 'scheduler' || currentPath !== routes.time_off_requests}
                                        optionFilterProp={'label'}
                                        className={`w-100 custom-antd-select ${(errors.staffId && touched.staffId )? 'border-danger' : ''}`}
                                        loading={userData.isLoading || userData.isFetching}
                                    />
                                    <ErrorMessage 
                                        className="formik-error" 
                                        name="staffId" 
                                        component="div" 
                                        render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} 
                                    />
                                </div>
                            )}
                        <div className="row">
                            <div className="mb-3 col-md-6">
                                <div className="help-small">
                                    <label className="form-label d-flex">
                                        <JTranslation typeCase="pascal" text={"From Date"} />
                                        <span className="mandatory ">*</span>
                                    </label>
                                </div>
                                <Field name="fromDate">
                                    {({ field, form }: FieldProps) => (
                                        <DatePickerComponent
                                            isClearable={false}
                                            selectedDate={field.value}
                                            minDate={currentDate}
                                            maxDate={maxDate}
                                            numberOfUpcomingYears={1}
                                            customInput={
                                                <input
                                                    style={
                                                        errors.fromDate && touched.fromDate
                                                            ? { border: '1px solid red' }
                                                            : undefined
                                                    }
                                                    className="form-control"
                                                    type="text"
                                                    maxLength={10}
                                                    autoComplete="off"
                                                />
                                            }
                                            setDate={(value) => form.setFieldValue(field.name, value)}
                                        />
                                    )}
                                </Field>
                                <ErrorMessage className="formik-error" name="fromDate" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                            </div>
                            {timeOffData.type !== 'time-off' && (
                                <div className="mb-3 col-md-6">
                                    <div className="help-small">
                                        <label className="form-label d-flex">
                                            <JTranslation typeCase="pascal" text={"To Date"} />
                                            <span className="mandatory ">*</span>
                                        </label>
                                    </div>
                                    <Field name="toDate">
                                        {({ field, form }: FieldProps) => (
                                            <DatePickerComponent
                                                isClearable={false}
                                                selectedDate={field.value}
                                                minDate={currentDate}
                                                maxDate={maxDate}
                                                numberOfUpcomingYears={1}
                                                customInput={
                                                    <input
                                                        style={
                                                            errors.toDate && touched.toDate
                                                                ? { border: '1px solid red' }
                                                                : undefined
                                                        }
                                                        className="form-control"
                                                        type="text"
                                                        maxLength={10}
                                                        autoComplete="off"
                                                    />
                                                }
                                                setDate={(value) => form.setFieldValue(field.name, value)}
                                            />
                                        )}
                                    </Field>
                                    <ErrorMessage className="formik-error" name="toDate" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                                </div>
                            )}
                        </div>
                        {timeOffData.type === 'time-off' && (
                            <div className="row mb-3">
                                <div className="mb-3 col-md-6">
                                    <div className="help-small">
                                        <label className="form-label d-flex">
                                            <JTranslation typeCase="pascal" text={FROM} />
                                            <span className="mandatory ">*</span>
                                        </label>
                                    </div>
                                    <Field name="startTime">
                                        {({ field, form }: FieldProps) => (
                                            <MlxTimePicker
                                                className='form-control'
                                                placeholder={translationText.startTime}
                                                // Bind the value and onChange handler
                                                value={parseTimeForTimePicker({ formattedTime: field.value })}
                                                onChange={(value) => form.setFieldValue(field.name, value)}
                                                style={
                                                    errors.startTime && touched.startTime
                                                        ? { border: '1px solid red' }
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </Field>
                                    <ErrorMessage className="formik-error" name="startTime" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                                </div>
                                <div className="mb-3 col-md-6">
                                    <div className="help-small">
                                        <label className="form-label d-flex">
                                            <JTranslation typeCase="pascal" text={TO} />
                                            <span className="mandatory ">*</span>
                                        </label>
                                    </div>
                                    <Field name="endTime">
                                        {({ field, form }: FieldProps) => (
                                            <MlxTimePicker
                                                className='form-control'
                                                placeholder={translationText.endTime}
                                                // Bind the value and onChange handler
                                                value={parseTimeForTimePicker({ formattedTime: field.value })}
                                                onChange={(value) => form.setFieldValue(field.name, value)}
                                                style={
                                                    errors.endTime && touched.endTime
                                                        ? { border: '1px solid red' }
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </Field>
                                    <ErrorMessage className="formik-error" name="endTime" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                                </div>
                            </div>
                        )}
                        {mandatoryError && (
                            <Message 
                                className='mb-2 px-2 py-1'
                                severity="error" 
                                text={<small><JTranslation typeCase="capitalize" text={`Selected dates include mandatory workdays. Please choose other dates.`} /></small>}
                            />
                        )}
                        <div className="mb-3">
                            <label className="form-label d-flex">{timeOffData.type === 'time-off'
                                ? <JTranslation typeCase="pascal" text={HOURS} />
                                : <JTranslation typeCase="pascal" text={DAYS} />
                            }</label>
                            <Field
                                style={errors.duration && touched.duration ? { border: '1px solid red' } : undefined}
                                name="duration"
                                className="form-control"
                                type="text"
                                autoComplete="off"
                                maxLength="250"
                                disabled
                            />
                            <ErrorMessage className="formik-error" name="duration" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label d-flex">
                                <JTranslation typeCase="pascal" text={REASON} />
                                <span className="mandatory ">*</span>
                            </label>
                            <Field
                                style={errors.reason && touched.reason ? { border: '1px solid red' } : undefined}
                                name="reason"
                                as="textarea"
                                className="form-control reason"
                                type="text"
                                autoComplete="off"
                                maxLength="1000"
                            />
                            <ErrorMessage className="formik-error" name="reason" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                        </div>

                        {currentPath === routes.time_off_requests &&
                            checkPermission(queryClient, PermissionKeys.MANAGE_TIME_OFF) && (
                                <div className="mb-3">
                                    <label className="form-label d-flex"><JTranslation typeCase="pascal" text={"Note"} /></label>
                                    <Field
                                        style={errors.note && touched.note ? { border: '1px solid red' } : undefined}
                                        name="note"
                                        as="textarea"
                                        className="form-control reason"
                                        type="text"
                                        autoComplete="off"
                                        maxLength="1000"
                                    />
                                    <ErrorMessage className="formik-error" name="note" component="div" render={(error) => <span className='formik-error'><JTranslation typeCase="pascal" text={error} /></span>} />
                                </div>
                            )}

                        {parent === 'scheduler' && pageMode === PageMode.EDIT &&
                            <div className='d-flex justify-content-between'>
                                {timeOffData?.actionStatus === 'PENDING' && (
                                    <button
                                        className="btn  btn-success"
                                        type="button"
                                        onClick={(event) => {
                                            confirmDialog({
                                                tagKey: 'time-off-confirm-request',
                                                className: 'custom-confirm-dialog',
                                                message: <JTranslation typeCase="capitalize" text={'Are you sure you want to approve this request?'} />,
                                                icon: 'pi pi-info-circle',
                                                acceptClassName: 'p-button-success',
                                                rejectClassName: 'p-button-danger',
                                                draggable: false,
                                                accept: () => {
                                                    setFieldValue('action', 'approve'); // Set action to "approve"
                                                    handleSubmit(); // Trigger Formik submit
                                                },
                                                reject: () => { },
                                                visible: true,
                                            });
                                        }}
                                    >
                                        <JTranslation typeCase="pascal" text={'Approve'} />
                                    </button>
                                )}
                                {(timeOffData?.actionStatus === 'APPROVED') && (
                                    <button
                                        className="btn btn-danger"
                                        type="button"
                                        onClick={() => {
                                            setFieldValue('action', 'cancel'); // Set action to "cancel"
                                            setWarningType('cancel');
                                            handleSubmit(); // Trigger Formik submit
                                        }}
                                    >
                                        <JTranslation typeCase="pascal" text={'Cancel'} />
                                    </button>
                                )}
                                {(timeOffData?.actionStatus === 'PENDING') && (
                                    <button
                                        className="btn btn-danger"
                                        type="button"
                                        onClick={() => {
                                            setFieldValue('action', 'reject'); // Set action to "reject"
                                            setWarningType('reject');
                                            handleSubmit(); // Trigger Formik submit
                                        }}
                                    >
                                        <JTranslation typeCase="pascal" text={'Reject'} />
                                    </button>
                                )}
                            </div>
                        }

                        {/* action button */}
                        <div className="save-btn-section shadow save-btn-absolute">
                            <button className="btn btn-custom-primary-outline" type="reset" onClick={() => onClose()}>
                                <JTranslation typeCase="pascal" text={CANCEL} />
                            </button>

                            <button 
                                className="btn btn-custom-primary" 
                                type="submit" 
                                disabled={
                                    userData.isLoading || 
                                    userData.isFetching || 
                                    mandatoryDaysQuery.isFetching || 
                                    ((currentPath !== routes.time_off_requests && parent !== 'scheduler') && mandatoryError)
                                }>
                                <JTranslation typeCase="pascal" text={submitButtonName} />
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    )
}

export default TimeOffRequestForm
